import React, { useState, useEffect, useCallback } from 'react';
import { ResponsiveButton } from '../../../common/ResponsiveLayout';
import * as assistantConfigService from '../../../../services/assistantConfigService';
import * as userService from '../../../../services/userService';
import AssistantList from './AssistantList';
import AssistantModal from './AssistantModal';
import toast from 'react-hot-toast';

/**
 * AssistantsTab Component
 * Manages the list of Vapi assistants
 */
const AssistantsTab = () => {
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  // For the list's "Assigned to" column; undefined until loaded.
  const [coachesById, setCoachesById] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    userService.getCoaches().then(({ data }) => {
      if (!cancelled) setCoachesById(new Map((data || []).map(c => [c.id, c])));
    });
    return () => { cancelled = true; };
  }, []);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // null, true, or false

  const fetchAssistants = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await assistantConfigService.getAssistants({
        search: searchTerm,
        isActive: activeFilter
      });

      if (fetchError) {
        throw new Error(fetchError);
      }
      
      setAssistants(data || []);
    } catch (err) {
      console.error('Failed to fetch assistants:', err);
      setError(err.message || 'Failed to fetch assistants');
      toast.error('Failed to fetch assistants');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  const handleCreate = async (assistantData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      const { data, error } = await assistantConfigService.createAssistant(assistantData);

      if (error) {
        throw new Error(error);
      }

      toast.success('Assistant created successfully!');
      // The row saved, but the automatic Retell sync behind it may not have.
      // createAssistant merges that failure onto `data` (and the sync function
      // records it on the row), so surface it instead of dropping it -- the
      // save itself still counts as successful.
      if (data?.retell_sync_error) {
        toast.error(`Saved, but Retell sync failed: ${data.retell_sync_error}`);
      }
      await fetchAssistants();
      setIsModalOpen(false);
      setSelectedAssistant(null);
    } catch (err) {
      console.error('Failed to create assistant:', err);
      setError(err.message || 'Failed to create assistant');
      toast.error(err.message || 'Failed to create assistant');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUpdate = async (assistantId, assistantData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      const { data, error } = await assistantConfigService.updateAssistant(assistantId, assistantData);

      if (error) {
        throw new Error(error);
      }

      toast.success('Assistant updated successfully!');
      // See handleCreate: a failed Retell sync must not be invisible.
      if (data?.retell_sync_error) {
        toast.error(`Saved, but Retell sync failed: ${data.retell_sync_error}`);
      }
      await fetchAssistants();
      setIsModalOpen(false);
      setSelectedAssistant(null);
    } catch (err) {
      console.error('Failed to update assistant:', err);
      setError(err.message || 'Failed to update assistant');
      toast.error(err.message || 'Failed to update assistant');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDelete = async (assistant) => {
    if (!window.confirm(`Are you sure you want to delete "${assistant.name}"? This action cannot be undone.`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await assistantConfigService.deleteAssistant(assistant.id);

      if (error) {
        throw new Error(error);
      }

      toast.success('Assistant deleted successfully!');
      await fetchAssistants();
    } catch (err) {
      console.error('Failed to delete assistant:', err);
      setError(err.message || 'Failed to delete assistant');
      toast.error(err.message || 'Failed to delete assistant');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleToggleStatus = async (assistant) => {
    const newStatus = !assistant.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} "${assistant.name}"?`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await assistantConfigService.toggleAssistantStatus(assistant.id, newStatus);

      if (error) {
        throw new Error(error);
      }

      toast.success(`Assistant ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      await fetchAssistants();
    } catch (err) {
      console.error(`Failed to ${action} assistant:`, err);
      setError(err.message || `Failed to ${action} assistant`);
      toast.error(err.message || `Failed to ${action} assistant`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDuplicate = (assistant) => {
    setDuplicateConfirm(assistant);
  };

  const confirmDuplicate = async () => {
    if (!duplicateConfirm) return;

    setOperationLoading(true);
    setError(null);

    try {
      // Create a copy of the assistant data
      const { id, vapi_id, created_at, created_by, ...assistantData } = duplicateConfirm;
      
      // Modify the name to add " - copy"
      const duplicatedData = {
        ...assistantData,
        name: `${assistantData.name} - copy`,
        coach_id: null, // Reset user assignment to "No Assignment"
        is_default: false, // Duplicates should never be default to avoid unique constraint violation
        // Extract tool IDs from tools array if it exists
        tool_ids: assistantData.tools?.map(tool => tool.id) || []
      };

      const { error } = await assistantConfigService.createAssistant(duplicatedData);

      if (error) {
        throw new Error(error);
      }

      toast.success('Assistant duplicated successfully!');
      await fetchAssistants();
      setDuplicateConfirm(null);
    } catch (err) {
      console.error('Failed to duplicate assistant:', err);
      setError(err.message || 'Failed to duplicate assistant');
      toast.error(err.message || 'Failed to duplicate assistant');
    } finally {
      setOperationLoading(false);
    }
  };

  const cancelDuplicate = () => {
    setDuplicateConfirm(null);
  };

  const handleEdit = (assistant) => {
    setSelectedAssistant(assistant);
    setIsModalOpen(true);
    setError(null);
  };

  const handleCloseModal = () => {
    setSelectedAssistant(null);
    setIsModalOpen(false);
    setError(null);
  };

  const handleSave = async (formData) => {
    if (selectedAssistant) {
      await handleUpdate(selectedAssistant.id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  return (
    <div>
      {/* Header with Actions */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="Search assistants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={activeFilter === null ? 'all' : activeFilter.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setActiveFilter(value === 'all' ? null : value === 'true');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
        
        <ResponsiveButton
          onClick={() => {
            setSelectedAssistant(null);
            setIsModalOpen(true);
            setError(null);
          }}
          variant="primary"
          disabled={operationLoading}
          size={{ xs: 'md', lg: 'md' }}
          className="w-full sm:w-auto"
        >
          Add New Assistant
        </ResponsiveButton>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex">
            <div className="flex-1">
              <strong>Error:</strong> {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900 ml-2 font-bold text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Assistant List */}
      <AssistantList
        assistants={assistants}
        coachesById={coachesById}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onDuplicate={handleDuplicate}
      />

      {/* Duplicate Confirmation Modal */}
      {duplicateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Duplicate Assistant
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to duplicate "<strong>{duplicateConfirm.name}</strong>"?
              This will create a copy with " - copy" added to the name and the user assignment
              will be reset to "No Assignment".
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDuplicate}
                disabled={operationLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDuplicate}
                disabled={operationLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {operationLoading ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assistant Modal */}
      <AssistantModal
        isOpen={isModalOpen}
        assistant={selectedAssistant}
        onSave={handleSave}
        onCancel={handleCloseModal}
        loading={operationLoading}
      />
    </div>
  );
};

export default AssistantsTab;